import React, { ChangeEvent } from "react";
import ReactModal from "react-modal";

import { V2 } from "../../Api";
import { RouteComponentPropsHistory } from "../../types";
import { formatDateTimeLocalNumeric, formatName, formatTelephone, truncateEllipse } from "../../Utilities";
import { IAuthProps } from "../AuthContext";

import DelayInput from "../DelayInput";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";

import withAuth from "../withAuth";
import withTitle from "../withTitle";
import FileUploadDialog from "../FileUploadDialog";
import HasRight from "../HasRight";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";

type ReminderGroup = "" | "hour" | "day" | "week";

interface IMessagingSentRemindersViewState {
    isLoading: boolean | string;
    isUploadSelectionDialogOpen: boolean;
    isUploadDialogOpen: boolean;
    isDeleteDialogOpen: boolean;
    reminders: any[];
    canViewMessages: boolean;

    searchOffset: number;
    searchText: string;
    searchDate: string;
    searchStatus: string;
    searchMessageId: string;

    reminderUploadType: ReminderGroup;
    reminderToDelete?: any;
}

interface IMessagingSentRemindersViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps {

}

const PAGE_SIZE = 50;

class MessagingSentRemindersView extends React.Component<IMessagingSentRemindersViewProps, IMessagingSentRemindersViewState> {
    constructor(props: Readonly<IMessagingSentRemindersViewProps>) {
        super(props);

        const state: IMessagingSentRemindersViewState = {
            isLoading: true,
            isUploadSelectionDialogOpen: false,
            isUploadDialogOpen: false,
            isDeleteDialogOpen: false,
            reminders: [],
            canViewMessages: props.authContext.hasRight("msg:read"),

            searchOffset: 0,
            searchText: "",
            searchDate: "",
            searchStatus: "",
            searchMessageId: "",

            reminderUploadType: "",
        };

        if (this.props.location.state) {
            state.searchOffset = this.props.location.state.searchOffset || 0;
            state.searchText = this.props.location.state.searchText || "";
            state.searchDate = this.props.location.state.searchDate || "";
            state.searchStatus = this.props.location.state.searchStatus || "";
            state.searchMessageId = this.props.location.state.searchMessageId || "";
        }

        this.state = state;

        this.loadReminders = this.loadReminders.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
        this.saveSearchState = this.saveSearchState.bind(this);
        this.toggleUploadDialog = this.toggleUploadDialog.bind(this);
        this.showUploadDialog = this.showUploadDialog.bind(this);
        this.hideUploadDialog = this.hideUploadDialog.bind(this);
        this.uploadSuccess = this.uploadSuccess.bind(this);
        this.uploadModeToTitle = this.uploadModeToTitle.bind(this);
        this.deleteReminder = this.deleteReminder.bind(this);
        this.realDeleteReminder = this.realDeleteReminder.bind(this);
        this.toggleDeleteDialog = this.toggleDeleteDialog.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
    }

    public componentDidMount() {
        this.loadReminders();
    }

    public render() {
        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <ToolbarMenuItem right="remind:send">
                        <button type="button" className="icon-upload" title="Upload reminders..." onClick={this.toggleUploadDialog} />
                    </ToolbarMenuItem>
                </ViewToolbar>
                <FileUploadDialog
                    isOpen={this.state.isUploadDialogOpen}
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    showProgress={true} descriptionOverride="-"
                    closeOnSuccess={true}
                    title={`Upload ${this.uploadModeToTitle(this.state.reminderUploadType)} reminders`}
                    extraData={{ group: this.state.reminderUploadType }}
                    onRequestClose={this.hideUploadDialog} onUploadSuccess={this.uploadSuccess}
                    endpoint="messaging/reminders/upload">
                </FileUploadDialog>
                <ReactModal
                    isOpen={this.state.isUploadSelectionDialogOpen} onRequestClose={this.toggleUploadDialog}
                    overlayClassName="modal-overlay" className="modal-content modal-larger" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open"
                    contentLabel="Upload reminders">
                    <div className="input-form">
                        <section className="info-block">
                            <header>
                                <h3>Upload reminders</h3>
                            </header>

                            <p>
                                Which reminders are you uploading?
                            </p>

                            <div className="action-panel-dialog">
                                <button type="button" className="button" onClick={() => this.showUploadDialog("hour")}>Hour before</button>
                                <button type="button" className="button" onClick={() => this.showUploadDialog("day")}>Day before</button>
                                <button type="button" className="button" onClick={() => this.showUploadDialog("week")}>Week before</button>
                            </div>
                        </section>
                    </div>
                </ReactModal>
                <DeletionConfirmationDialog
                    isOpen={this.state.isDeleteDialogOpen}
                    description={null}
                    onRequestClose={this.toggleDeleteDialog}
                    onSuccess={this.loadReminders}
                    onConfirm={this.realDeleteReminder}>
                    {this.state.reminderToDelete ?
                        <>
                            <p>
                                Are you sure you want to delete this reminder?
                            </p>
                            <p>
                                {formatName(this.state.reminderToDelete.patientName)} ({this.state.reminderToDelete.patientId})<br />
                                on {formatDateTimeLocalNumeric(this.state.reminderToDelete.appointmentTimestamp)}
                            </p>
                        </>
                        : null}
                </DeletionConfirmationDialog>
                <div className="filter-control-box">
                    <DelayInput timeout={300} type="search" className="fill-space" name="searchText" value={this.state.searchText} onChangeDelayed={this.handleElementChange} placeholder="Search..." disabled={!!this.state.searchMessageId} />
                    <div className="control-group">
                        <span>Day Sent:</span>
                        <DelayInput timeout={300} type="date" name="searchDate" value={this.state.searchDate} onChangeDelayed={this.handleElementChange} />
                    </div>
                    <select name="searchStatus" value={this.state.searchStatus} onChange={this.handleElementChange}>
                        <option value="">All Status</option>
                        <option value="notsent">Not Sent</option>
                        <option value="sent">Sent</option>
                        <option value="delivered">Delivered</option>
                        {/*<option value="confirmed">Confirmed</option>
                        <option value="canceled">Canceled</option>*/}
                        <option value="deleted">Deleted</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Date &amp; Time</th>
                            <th>Appointment</th>
                            <th>Location</th>
                            <th>Chart #</th>
                            <th>Patient Name</th>
                            <th>Phone #</th>
                            <th colSpan={2}>Status</th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Sent Reminders</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.isLoading === true
                            ? <tr>
                                <td colSpan={10}><div className="spinner spinner-center lds-dual-ring" /></td>
                            </tr>
                            : typeof this.state.isLoading === "string"
                                ? <tr>
                                    <td colSpan={10}><span className="error-text">{this.state.isLoading}</span></td>
                                </tr>
                                : this.state.reminders.length > 0
                                    ? this.state.reminders.map(a => <tr key={a.id} className={(this.state.canViewMessages ? "clickable" : undefined) + " " + (this.statusToClass(a.status))} onClick={this.state.canViewMessages ? () => this.goToThread(a.threadId) : undefined}>
                                        <td data-caption="Date & Time: ">{formatDateTimeLocalNumeric(a.creationTimestamp)}</td>
                                        <td data-caption="Appointment: ">{formatDateTimeLocalNumeric(a.appointmentTimestamp)}</td>
                                        <td data-caption="Location: ">{truncateEllipse(a.appointmentLocation, 30)}</td>
                                        <td data-caption="Chart #: ">{a.patientId}</td>
                                        <td data-caption="Patient: ">{formatName(a.patientName)}</td>
                                        <td data-caption="Phone #: ">{formatTelephone(a.destination)}</td>
                                        <td data-caption="Status: ">{this.statusToText(a.status)}</td>
                                        <td className="col-nopad col-smallest">
                                            <HasRight right="remind:send">
                                                {a.status === 0
                                                    ? <button type="button" className="button btn-light red small" onClick={(e) => { e.stopPropagation(); this.deleteReminder(a); }}><i className="icon-trash-o" /></button>
                                                    : null}
                                            </HasRight>
                                        </td>
                                    </tr>)
                                    : <tr>
                                        <td colSpan={10}>No reminders</td>
                                    </tr>}
                    </tbody>
                </table>
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.reminders.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        } as any, () => this.loadReminders());
    }

    private async loadReminders() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters = [
                `offset=${this.state.searchOffset}`,
                `limit=${PAGE_SIZE}`,
            ];

            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchDate) {
                parameters.push(`date=${this.state.searchDate}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
            }
            if (this.state.searchMessageId) {
                parameters.push(`message_id=${this.state.searchMessageId}`);
            }

            const response = await V2.get<any>(`messaging/reminders?${parameters.join("&")}`);

            this.setState({
                isLoading: false,
                reminders: response.reminders
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load reminders"
            });
        }
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchOffset: this.state.searchOffset,
            searchText: this.state.searchText,
            searchDate: this.state.searchDate,
            searchStatus: this.state.searchStatus
        };

        this.props.history.replace(location);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.loadReminders());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.loadReminders());
    }

    private goToThread(threadId: string) {
        this.props.history.push(`/reminders/messages/${threadId}`);
    }

    private statusToText(status: number) {
        switch (status) {
            case 0: return "Not Sent";
            case 1: return "Sent";
            case 2: return "Delivered";
            case 3: return "Confirmed";
            case 4: return "Canceled";
            case 5: return "Deleted";
            case 6: return "Failed";
            default: return status.toString();
        }
    }

    private toggleUploadDialog() {
        this.setState(s => ({
            isUploadSelectionDialogOpen: !s.isUploadSelectionDialogOpen
        }));
    }

    private showUploadDialog(mode: ReminderGroup) {
        this.setState({
            isUploadSelectionDialogOpen: false,
            isUploadDialogOpen: true,
            reminderUploadType: mode
        });
    }

    private hideUploadDialog() {
        this.setState({
            isUploadDialogOpen: false
        });
    }

    private uploadSuccess(results: any[]) {
        // TODO: Show processing status dialog?
    }

    private uploadModeToTitle(type: ReminderGroup) {
        switch (type) {
            case "day": return "day before";
            case "hour": return "hour before";
            case "week": return "week before";
            default: return "(unknown)";
        }
    }

    private deleteReminder(reminder: any) {
        this.setState({
            isDeleteDialogOpen: true,
            reminderToDelete: reminder,
        });
    }

    private toggleDeleteDialog() {
        const isDeleteDialogOpen = this.state.isDeleteDialogOpen;
        this.setState({
            isDeleteDialogOpen: !isDeleteDialogOpen
        });
    }

    private async realDeleteReminder() {
        const data = {
            status: "deleted"
        };

        await V2.patch(`messaging/reminders/${this.state.reminderToDelete.id}`, data);

        this.setState({
            reminderToDelete: undefined
        });
    }

    private statusToClass(status: number) {
        switch (status) {
            case 5: return "row-gray-text";
            default: return "";
        }
    }
}

export default withTitle("Appointment Reminders", withAuth(MessagingSentRemindersView));
